import React, { useState } from 'react';
import { Pencil, X, Check, AlertCircle } from 'lucide-react';

// =====================================================================
// Chamados — o painel de PEÇAS do chamado (Julia, 2026-09-01).
//
// Mostra as duas listas que moram na `dtbChamadoPeca` e deixa o TI completar a
// cotação depois.
//
// ---------------------------------------------------------------------
// POR QUE ELE EXISTE
// ---------------------------------------------------------------------
// Até 31/08 o fornecedor era obrigatório na finalização. Em 01/09 virou
// opcional, porque quem sabe de quem comprar é o **Gerente de TI**, no
// escritório — não o técnico de pé na loja com o equipamento aberto. Exigir
// dele produzia `"a definir"` digitado para conseguir fechar a tela, que é pior
// que vazio, porque **parece preenchido**.
//
// ⚠️⚠️ TIRAR A TRAVA SEM PÔR NADA NO LUGAR TRANSFORMARIA "depois" EM "nunca".
//    Por isso este painel faz DUAS coisas, e a segunda é tão importante quanto a
//    primeira: deixa preencher, e **cobra o que falta**, em vermelho, à vista de
//    quem abrir o chamado. A cobrança é a fila, não o formulário — o mesmo
//    princípio do "parado há N dias".
//
// ---------------------------------------------------------------------
// AS DUAS LISTAS
// ---------------------------------------------------------------------
// ⚠️ `Compra` e `Reparo` dividem a tabela mas NÃO a leitura: numa se adquire
//    (tem quantidade, valor e orçamento), na outra o equipamento que já é nosso
//    está na bancada de um fornecedor (só produto e onde está). Uma tabela só,
//    com metade das colunas vazias, faria o reparo parecer compra malpreenchida.
//
// ⚠️ O REPARO NÃO ENTRA NO ORÇAMENTO nem na cobrança de cotação — o fornecedor
//    dele é obrigatório na origem, e preço de conserto não é campo deste
//    registro. Somá-lo o faria entrar como zero ("R$ 0,00 neste conserto").
// =====================================================================

const COR = 'var(--ch-cor)';
// ⚠️ As variantes translúcidas são variáveis PRÓPRIAS: `var(--ch-cor)22` é CSS
//    inválido — sufixo de alfa não se concatena a uma função. Ver o bloco
//    "MÓDULO CHAMADOS — A COR DA FILA" no index.css.
const COR_A44 = 'var(--ch-cor-aa44)';   // o mesmo matiz, translúcido
const COR_CLARA = 'var(--ch-cor-clara)';
const API = '/api/chamados';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const fmtMoeda = (v) => (v == null || v === ''
    ? '—'
    : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));

const inputStyle = {
    padding: '7px 9px', borderRadius: 6, border: '1px solid #cbd5e1',
    fontSize: 13, color: '#0f172a', outline: 'none', boxSizing: 'border-box',
};

const th = { padding: '4px 8px', fontWeight: 700, textAlign: 'left' };
/** ⚠️ `tabular-nums`: sem isso os dígitos dançam e a coluna deixa de se ler como coluna. */
const tdNum = { padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

/**
 * Soma do orçamento. `null` quando NENHUMA peça tem valor.
 *
 * ⚠️⚠️ ESPELHA o `totalDasPecas` do servidor, e o `null` é a parte que importa:
 *    "R$ 0,00" se lê como "não custa nada", e o que se quer dizer é "ainda não
 *    sabemos".
 */
function totalDasCompras(lista) {
    const comValor = lista.filter((x) => x.VALOR_UNITARIO != null);
    if (!comValor.length) return null;
    return lista.reduce((s, x) => s + Number(x.VALOR_UNITARIO || 0) * Number(x.QUANTIDADE || 0), 0);
}

/**
 * O painel.
 *
 * @param {object[]} pecas     como vieram do `GET /:id` (colunas em MAIÚSCULAS)
 * @param {number}   idChamado
 * @param {boolean}  podeEditar quem executa (Gerente TI, TI, Admin, Diretoria)
 * @param {string}   status     do chamado — `Cancelado` fecha a edição
 * @param {Function} aoSalvar   recarrega o detalhe depois de gravar
 */
export default function ChamadosPecas({ pecas, idChamado, podeEditar, status, aoSalvar }) {
    const compras = (pecas || []).filter((x) => (x.TIPO || 'Compra') === 'Compra');
    const reparos = (pecas || []).filter((x) => x.TIPO === 'Reparo');

    // ⚠️ `null` = ninguém está editando. Guardar o TIPO em edição (e não um
    //    booleano) é o que permite as duas listas terem o próprio botão sem uma
    //    abrir a outra.
    const [editando, setEditando] = useState(null);
    const [linhas, setLinhas] = useState([]);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');

    // ⚠️⚠️ A COTAÇÃO PENDENTE É CONTADA E MOSTRADA — é a contrapartida de ter
    //    tornado o fornecedor opcional. Só a COMPRA entra: o reparo não tem
    //    cotação a completar.
    const semFornecedor = compras.filter((x) => !String(x.FORNECEDOR || '').trim()).length;
    const semValor = compras.filter((x) => x.VALOR_UNITARIO == null).length;

    // ⚠️ Cancelado fecha a edição — não há mais o que comprar. Encerrado e
    //    Validado NÃO fecham, de propósito: a nota e o orçamento chegam tarde, e
    //    congelar antes devolveria o número ao WhatsApp.
    const editavel = podeEditar && status !== 'Cancelado';

    const abrirEdicao = (tipo, lista) => {
        setErro('');
        setEditando(tipo);
        // As linhas viram texto para caber nos inputs; `null` vira '' (o input é
        // controlado e `null` o tornaria não-controlado).
        setLinhas(lista.map((x) => ({
            descricao: x.DESCRICAO || '',
            quantidade: x.QUANTIDADE == null ? '' : String(Number(x.QUANTIDADE)),
            valor_unitario: x.VALOR_UNITARIO == null ? '' : String(Number(x.VALOR_UNITARIO)).replace('.', ','),
            fornecedor: x.FORNECEDOR || '',
        })));
    };

    const mudar = (i, campo, valor) => setLinhas((ls) => ls.map((l, j) => (j === i ? { ...l, [campo]: valor } : l)));

    const salvar = async () => {
        setSalvando(true); setErro('');
        try {
            const r = await fetch(`${API}/${idChamado}/pecas`, {
                method: 'PUT',
                headers: { ...auth(), 'Content-Type': 'application/json' },
                // ⚠️ Manda a lista INTEIRA: a rota substitui o tipo em edição.
                //    Mandar só o que mudou faria o servidor apagar o resto.
                body: JSON.stringify({ tipo: editando, pecas: linhas }),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.message || 'Não foi possível salvar as peças.');
            setEditando(null);
            if (aoSalvar) await aoSalvar();
        } catch (e) {
            setErro(e.message);
        } finally {
            setSalvando(false);
        }
    };

    if (!compras.length && !reparos.length) return null;

    const botaoEditar = (tipo, lista) => editavel && editando !== tipo && (
        <button
            type="button"
            onClick={() => abrirEdicao(tipo, lista)}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 10,
                background: 'none', border: `1px solid ${COR_A44}`, color: COR,
                borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}
        >
            <Pencil size={12} /> {tipo === 'Compra' ? 'Completar cotação' : 'Editar'}
        </button>
    );

    const barraDeEdicao = (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" onClick={salvar} disabled={salvando}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                    backgroundColor: COR, color: '#fff', border: 'none',
                    borderRadius: 6, padding: '7px 14px', fontSize: 12, fontWeight: 700,
                }}>
                <Check size={13} /> {salvando ? 'Salvando…' : 'Salvar'}
            </button>
            <button type="button" onClick={() => { setEditando(null); setErro(''); }} disabled={salvando}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                    background: 'none', color: '#64748b', border: '1px solid #cbd5e1',
                    borderRadius: 6, padding: '7px 14px', fontSize: 12, fontWeight: 600,
                }}>
                <X size={13} /> Cancelar
            </button>
        </div>
    );

    return (
        <>
            {erro && (
                <div style={{
                    display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12,
                    backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 10,
                }}>
                    <AlertCircle size={16} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
                    <p style={{ margin: 0, fontSize: 12, color: '#991b1b' }}>{erro}</p>
                </div>
            )}

            {/* ============================ PEÇAS A COMPRAR ==================== */}
            {!!compras.length && (
                <div style={{ marginBottom: 20 }}>
                    <p style={{
                        fontSize: 12, fontWeight: 800, color: '#64748b',
                        textTransform: 'uppercase', letterSpacing: '.04em', margin: '0 0 6px 0',
                    }}>
                        Peças a comprar {botaoEditar('Compra', compras)}
                    </p>

                    {/* ⚠️⚠️ A COBRANÇA. Ela substitui a trava que saiu do formulário —
                        sem alguém dizendo o que falta, "preenche depois" vira "nunca
                        preenchido" e o registro deixa de servir para comprar.
                        ⚠️ Diz QUEM costuma preencher: sem isso, quem lê a pendência
                        não sabe se a bola está com ele. */}
                    {(semFornecedor > 0 || semValor > 0) && editando !== 'Compra' && (
                        <p style={{
                            margin: '0 0 8px 0', fontSize: 12, color: '#b45309', lineHeight: 1.5,
                            backgroundColor: '#fffbeb', border: '1px solid #fde68a',
                            borderRadius: 6, padding: '7px 10px',
                        }}>
                            <strong>Falta cotação:</strong>
                            {semFornecedor > 0 && ` ${semFornecedor} sem fornecedor`}
                            {semFornecedor > 0 && semValor > 0 && ' ·'}
                            {semValor > 0 && ` ${semValor} sem valor`}.
                            {' '}Quem normalmente completa é o <strong>Gerente de TI</strong>.
                        </p>
                    )}

                    {editando === 'Compra' ? (
                        <>
                            {linhas.map((l, i) => (
                                <div key={i} style={{
                                    display: 'grid', gap: 8, marginBottom: 8,
                                    gridTemplateColumns: '2fr 0.7fr 1fr 1.4fr', alignItems: 'end',
                                }}>
                                    <div>
                                        {i === 0 && <label style={th}>Produto</label>}
                                        <input style={{ ...inputStyle, width: '100%' }} value={l.descricao}
                                            onChange={(e) => mudar(i, 'descricao', e.target.value)} />
                                    </div>
                                    <div>
                                        {i === 0 && <label style={th}>Qtd.</label>}
                                        <input style={{ ...inputStyle, width: '100%' }} inputMode="decimal"
                                            value={l.quantidade}
                                            onChange={(e) => mudar(i, 'quantidade', e.target.value)} />
                                    </div>
                                    <div>
                                        {i === 0 && <label style={th}>Valor un. (R$)</label>}
                                        <input style={{ ...inputStyle, width: '100%' }} inputMode="decimal"
                                            placeholder="opcional" value={l.valor_unitario}
                                            onChange={(e) => mudar(i, 'valor_unitario', e.target.value)} />
                                    </div>
                                    <div>
                                        {i === 0 && <label style={th}>Fornecedor</label>}
                                        <input style={{ ...inputStyle, width: '100%' }}
                                            placeholder="de quem comprar" value={l.fornecedor}
                                            onChange={(e) => mudar(i, 'fornecedor', e.target.value)} />
                                    </div>
                                </div>
                            ))}
                            {barraDeEdicao}
                        </>
                    ) : (
                        <>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                    <thead>
                                        <tr style={{ color: '#94a3b8', textAlign: 'left' }}>
                                            <th style={{ ...th, paddingLeft: 0 }}>Produto</th>
                                            <th style={{ ...th, textAlign: 'right' }}>Qtd.</th>
                                            <th style={{ ...th, textAlign: 'right' }}>Valor un.</th>
                                            <th style={{ ...th, paddingRight: 0 }}>Fornecedor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {compras.map((x) => (
                                            <tr key={x.ID} style={{ borderTop: `1px solid ${COR_CLARA}`, color: '#334155' }}>
                                                <td style={{ padding: '6px 8px 6px 0' }}>{x.DESCRICAO}</td>
                                                <td style={tdNum}>{x.QUANTIDADE == null ? '—' : Number(x.QUANTIDADE)}</td>
                                                {/* ⚠️ O que falta aparece como "a cotar" em cor de
                                                    alerta, não como travessão neutro: travessão se lê
                                                    como "não se aplica", e aqui se aplica e falta. */}
                                                <td style={{
                                                    ...tdNum,
                                                    color: x.VALOR_UNITARIO == null ? '#b45309' : '#334155',
                                                }}>
                                                    {x.VALOR_UNITARIO == null ? 'a cotar' : fmtMoeda(x.VALOR_UNITARIO)}
                                                </td>
                                                <td style={{
                                                    padding: '6px 0 6px 8px',
                                                    color: String(x.FORNECEDOR || '').trim() ? '#334155' : '#b45309',
                                                }}>
                                                    {String(x.FORNECEDOR || '').trim() || 'a definir'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <p style={{ margin: '8px 0 0 0', fontSize: 13, fontWeight: 700, color: '#334155' }}>
                                Orçamento: {fmtMoeda(totalDasCompras(compras))}
                                {/* ⚠️ O "parcial" não é enfeite: um total que ignora as peças
                                    sem cotação, mostrado como se fosse o orçamento inteiro,
                                    subestima a compra. */}
                                {semValor > 0 && semValor < compras.length && (
                                    <span style={{ fontWeight: 500, color: '#b45309' }}>
                                        {' '}— parcial, {semValor} sem valor
                                    </span>
                                )}
                            </p>
                        </>
                    )}
                </div>
            )}

            {/* ============================ PEÇAS EM REPARO ==================== */}
            {!!reparos.length && (
                <div style={{ marginBottom: 20 }}>
                    <p style={{
                        fontSize: 12, fontWeight: 800, color: '#64748b',
                        textTransform: 'uppercase', letterSpacing: '.04em', margin: '0 0 6px 0',
                    }}>
                        Peças em reparo {botaoEditar('Reparo', reparos)}
                    </p>
                    {/* ⚠️ SEM quantidade, valor e orçamento — não é omissão de tela: no
                        reparo esses campos não existem. Mostrá-los vazios faria o
                        conserto parecer uma compra malpreenchida. */}
                    {editando === 'Reparo' ? (
                        <>
                            {linhas.map((l, i) => (
                                <div key={i} style={{
                                    display: 'grid', gap: 8, marginBottom: 8,
                                    gridTemplateColumns: '2fr 1.6fr', alignItems: 'end',
                                }}>
                                    <div>
                                        {i === 0 && <label style={th}>Produto</label>}
                                        <input style={{ ...inputStyle, width: '100%' }} value={l.descricao}
                                            onChange={(e) => mudar(i, 'descricao', e.target.value)} />
                                    </div>
                                    <div>
                                        {i === 0 && <label style={th}>Fornecedor (onde está)</label>}
                                        <input style={{ ...inputStyle, width: '100%' }} value={l.fornecedor}
                                            onChange={(e) => mudar(i, 'fornecedor', e.target.value)} />
                                    </div>
                                </div>
                            ))}
                            {barraDeEdicao}
                        </>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                    <tr style={{ color: '#94a3b8', textAlign: 'left' }}>
                                        <th style={{ ...th, paddingLeft: 0 }}>Produto</th>
                                        <th style={{ ...th, paddingRight: 0 }}>Está com</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reparos.map((x) => (
                                        <tr key={x.ID} style={{ borderTop: `1px solid ${COR_CLARA}`, color: '#334155' }}>
                                            <td style={{ padding: '6px 8px 6px 0' }}>{x.DESCRICAO}</td>
                                            <td style={{ padding: '6px 0 6px 8px' }}>{x.FORNECEDOR || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
